/**
 * Bill Receipt Page
 *
 * Displays and prints a bill receipt.
 * Optimized for thermal printers (80mm width).
 * Supports: invoices, tickets, and proformas/quotations.
 */

import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Printer, ArrowRight, Pencil } from 'lucide-react'

import api from '@/lib/api'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/utils'

interface ReceiptItem {
  code: string
  description: string
  quantity: number
  unit_price: number
  total: number
}

interface ReceiptData {
  business_name: string
  business_email: string
  business_id: string
  business_phone: string
  business_location: string
  bill_id: number
  bill_number: string | null
  bill_type: string
  payment_type: string
  created_at: string
  expiration_date?: string | null
  client_name: string
  client_note?: string | null
  seller_name: string
  consecutivo_hacienda: string | null
  clave_hacienda: string | null
  items: ReceiptItem[]
  subtotal: number
  total_general_discount: number
  total_line_discount: number
  total_sale_tax: number
  total_deposited: number
  total: number
  total_client_pay: number
  total_client_change: number
}

export function BillReceiptPage() {
  const { billId } = useParams<{ billId: string }>()
  const navigate = useNavigate()
  const printRef = useRef<HTMLDivElement>(null)
  const [receipt, setReceipt] = useState<ReceiptData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check if this is a proforma
  const isProforma = receipt?.bill_type?.toLowerCase() === 'proforma'

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const response = await api.get(`/bills/${billId}/receipt`)
        setReceipt(response.data)
      } catch (err) {
        setError(t('receipts.errorLoading'))
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    if (billId) {
      fetchReceipt()
    }
  }, [billId])

  const handlePrint = () => {
    window.print()
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handleEdit = () => {
    navigate(`/bills/new?continue=${billId}`)
  }

  const handleConvertToInvoice = async () => {
    try {
      await api.post(`/bills/${billId}/convert-to-invoice`, {
        target_type: 'invoice',
      })
      navigate(`/bills/new?continue=${billId}`)
    } catch (err) {
      console.error('Error converting proforma:', err)
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error || !receipt) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">{error || t('receipts.notFound')}</p>
          <Button onClick={handleBack}>{t('common.back')}</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Print Controls - Hidden when printing */}
      <div className="no-print flex flex-wrap gap-2 mb-4 max-w-md mx-auto">
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>
        <Button onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          {t('receipts.print')}
        </Button>
        {isProforma && (
          <>
            <Button variant="outline" onClick={handleEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              {t('common.edit')}
            </Button>
            <Button onClick={handleConvertToInvoice}>
              <ArrowRight className="h-4 w-4 mr-2" />
              {t('proformas.convertToInvoice')}
            </Button>
          </>
        )}
      </div>

      {/* Receipt Content */}
      <div
        ref={printRef}
        className="receipt-container bg-white mx-auto p-4 shadow-lg"
        style={{ maxWidth: '80mm', fontFamily: 'Arial, sans-serif' }}
      >
        {/* Proforma Banner */}
        {isProforma && (
          <div className="text-center mb-2 py-2 bg-gray-100 border-2 border-dashed border-gray-400">
            <p className="text-lg font-bold">{t('receipts.printed.proformaBanner')}</p>
            <p className="text-xs text-gray-600">{t('receipts.printed.nonFiscal')}</p>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-lg font-bold">{receipt.business_name}</h1>
          <p className="text-xs">{receipt.business_email}</p>
          <p className="text-xs">{t('receipts.printed.idPrefix')} {receipt.business_id}</p>
          <p className="text-xs">{t('receipts.printed.phonePrefix')} {receipt.business_phone}</p>
          <p className="text-xs">{receipt.business_location}</p>
          {!isProforma && receipt.consecutivo_hacienda && (
            <>
              <p className="text-xs mt-2">{t('receipts.printed.consecutive')} {receipt.consecutivo_hacienda}</p>
              <p className="text-xs">{t('receipts.printed.key')}</p>
              <p className="text-xs break-all">{receipt.clave_hacienda || receipt.bill_id}</p>
            </>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-dashed border-gray-400 my-2"></div>

        {/* Bill Info */}
        <table className="w-full text-xs mb-2">
          <tbody>
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.type')}</th>
              <td className="text-right">{isProforma ? 'Proforma' : receipt.bill_type}</td>
            </tr>
            {!isProforma && (
              <tr>
                <th className="text-left font-semibold">{t('receipts.printed.billPayment')}</th>
                <td className="text-right">{receipt.payment_type}</td>
              </tr>
            )}
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.number')}</th>
              <td className="text-right">{isProforma ? `PRO-${receipt.bill_number || receipt.bill_id}` : (receipt.bill_number || receipt.bill_id)}</td>
            </tr>
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.date')}</th>
              <td className="text-right">{receipt.created_at}</td>
            </tr>
            {isProforma && receipt.expiration_date && (
              <tr>
                <th className="text-left font-semibold">{t('receipts.printed.validUntil')}</th>
                <td className="text-right">{receipt.expiration_date}</td>
              </tr>
            )}
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.client')}</th>
              <td className="text-right">
                {receipt.client_note
                  ? `${receipt.client_name} (${receipt.client_note})`
                  : receipt.client_name}
              </td>
            </tr>
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.seller')}</th>
              <td className="text-right">{receipt.seller_name}</td>
            </tr>
          </tbody>
        </table>

        {/* Divider */}
        <div className="border-t border-dashed border-gray-400 my-2"></div>

        {/* Items */}
        <table className="w-full text-xs mb-2">
          <thead>
            <tr className="border-b border-gray-300">
              <th className="text-left">{t('receipts.printed.product')}</th>
              <th className="text-center">#</th>
              <th className="text-right">{t('receipts.printed.price')}</th>
              <th className="text-right">{t('receipts.printed.amount')}</th>
            </tr>
          </thead>
          <tbody>
            {receipt.items.map((item, index) => (
              <tr key={index}>
                <td className="text-left" style={{ maxWidth: '100px', wordWrap: 'break-word' }}>
                  {(item.code + '-' + item.description).substring(0, 25)}
                </td>
                <td className="text-center">{item.quantity}</td>
                <td className="text-right">{formatCurrency(item.unit_price)}</td>
                <td className="text-right">{formatCurrency(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Divider */}
        <div className="border-t border-dashed border-gray-400 my-2"></div>

        {/* Totals */}
        <table className="w-full text-xs">
          <tbody>
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.subtotal')}</th>
              <td className="text-right">{formatCurrency(receipt.subtotal)}</td>
            </tr>
            {receipt.total_general_discount > 0 && (
              <tr>
                <th className="text-left font-semibold">{t('receipts.printed.generalDiscount')}</th>
                <td className="text-right">-{formatCurrency(receipt.total_general_discount)}</td>
              </tr>
            )}
            {receipt.total_line_discount > 0 && (
              <tr>
                <th className="text-left font-semibold">{t('receipts.printed.lineDiscount')}</th>
                <td className="text-right">-{formatCurrency(receipt.total_line_discount)}</td>
              </tr>
            )}
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.tax')}</th>
              <td className="text-right">{formatCurrency(receipt.total_sale_tax)}</td>
            </tr>
            {!isProforma && receipt.total_deposited > 0 && (
              <tr>
                <th className="text-left font-semibold">{t('receipts.printed.deposit')}</th>
                <td className="text-right">-{formatCurrency(receipt.total_deposited)}</td>
              </tr>
            )}
            <tr className="font-bold">
              <th className="text-left">{t('receipts.printed.total')}</th>
              <td className="text-right">{formatCurrency(receipt.total)}</td>
            </tr>
            {!isProforma && (
              <>
                <tr>
                  <th className="text-left font-semibold">{t('receipts.printed.payment')}</th>
                  <td className="text-right">{formatCurrency(receipt.total_client_pay)}</td>
                </tr>
                <tr className="font-bold">
                  <th className="text-left">{t('receipts.printed.change')}</th>
                  <td className="text-right">{formatCurrency(receipt.total_client_change)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>

        {/* Divider */}
        <div className="border-t border-dashed border-gray-400 my-2"></div>

        {/* Footer */}
        <div className="text-center text-xs mt-4">
          {isProforma ? (
            <>
              <p className="font-bold">{t('receipts.printed.thanksPreference')}</p>
              <p className="text-[10px] mt-2 text-gray-600">
                {t('receipts.printed.proformaDisclaimer')}
              </p>
              {receipt.expiration_date && (
                <p className="text-[10px] mt-1 text-gray-600">
                  {t('receipts.printed.validUntil')} {receipt.expiration_date}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="font-bold">{t('receipts.printed.thanksPurchase')}</p>
              <p className="text-[10px] mt-2 text-gray-600">
                {t('receipts.printed.legalNotice')}
              </p>
              <p className="text-[10px] mt-1 text-gray-600">{t('receipts.printed.version')}</p>
            </>
          )}
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .receipt-container {
            box-shadow: none !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 2mm !important;
          }
          @page {
            size: 80mm auto;
            margin: 0;
          }
        }
      `}</style>
    </div>
  )
}
